import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  RefreshCw,
  MailCheck,
  MailOpen,
  Clock,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import moment from 'moment';
import { getSentContracts } from '../../../api/dashboard';
import AgencyScopeSwitch, { useAgencyScope } from '../../../components/common/AgencyScopeSwitch';

/**
 * "Sent Contracts" — read receipts for Contracts of Sale the agent has emailed.
 *
 * Each contract email carries a tracking pixel, so this list shows whether the
 * buyer has opened it yet ("Opened" with a timestamp) or not ("Sent"). Lets the
 * agent see at a glance which buyers have actually looked at the contract.
 *
 * SCOPE. This showed every admin the whole office under a heading that reads as
 * personal, so Simon Abbott found four other agents' contracts on his own
 * dashboard: "please only just make it that individual agents can only see
 * there own sent contracts" (31 Aug 2026). It now defaults to your own sends
 * and offers admins a switch; the backend applies the same rule, so an agent
 * asking for the office list is scoped back regardless.
 */

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'opened', label: 'Opened' },
  { key: 'unopened', label: 'Not opened' },
];

const SentContractsPanel = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [aggregate, setAggregate] = useState({ sent: 0, opened: 0, unopened: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // Preview a handful of rows by default; "View all" expands into a
  // scrollable list so the dashboard stays short and easy to scroll past.
  const [showAll, setShowAll] = useState(false);
  const PREVIEW_COUNT = 5;

  const { agencyWide, setAgencyWide, canSwitch } = useAgencyScope('sentContracts');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (filter !== 'all') params.status = filter;
      // Always explicit. Omitting it lets the backend fall back to its own role
      // default, which would put the panel and this switch out of step.
      params.agency_wide = agencyWide ? 'true' : 'false';
      const res = await getSentContracts(params);
      setRows(res.data?.results || []);
      setAggregate(res.data?.aggregate || { sent: 0, opened: 0, unopened: 0 });
    } catch (err) {
      console.error('Error loading sent contracts:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, agencyWide]);

  useEffect(() => {
    load();
  }, [load]);

  // WHERE A ROW OPENS. Simon Abbott, 3 Sep 2026: "Sent Contracts need to be
  // clickable and editable from here." eSign sends are mirrored into this list
  // with no linked property, so those rows had nothing to navigate to and did
  // nothing when clicked, and read "Property" with no address. An eSign row now
  // opens its envelope - where the contract can be viewed, resent, its captured
  // details edited, or (while still a draft) reopened in the editor. Rows sent
  // from a property's Buyers tab keep opening that property.
  const targetFor = (row) => {
    if (row.envelope_id) return `/esign/document/${row.envelope_id}`;
    if (row.property_id) return `/properties/${row.property_id}`;
    return null;
  };

  const addressFor = (row) =>
    row.property_address || row.envelope_title || 'Contract of Sale';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-teal-500" />
          <h2 className="text-sm sm:text-base font-bold text-gray-800">Sent Contracts</h2>
          {aggregate.sent > 0 && (
            <span className="text-[11px] font-medium text-gray-500">
              {aggregate.opened}/{aggregate.sent} opened
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {canSwitch && (
            <AgencyScopeSwitch
              agencyWide={agencyWide}
              onChange={setAgencyWide}
              mineLabel="Mine"
              agencyLabel="All agents"
              className="mr-1"
            />
          )}
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-2.5 py-1 text-[11px] rounded-md font-medium transition-colors ${
                filter === f.key
                  ? 'bg-teal-50 text-teal-600'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            onClick={load}
            className="p-1.5 text-gray-400 hover:text-teal-600 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className={`space-y-2 overflow-y-auto no-scrollbar pr-1 ${showAll ? 'max-h-[420px]' : 'max-h-[300px]'}`}>
        {loading && rows.length === 0 ? (
          <div className="py-8 text-center text-gray-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm">
            {agencyWide
              ? 'No contracts sent by anyone in the office yet.'
              : "No contracts sent yet. Send a Contract of Sale from a property's Buyers tab, "
                + 'an open home, or eSign, and its read status will show up here.'}
          </div>
        ) : (
          (showAll ? rows : rows.slice(0, PREVIEW_COUNT)).map((row) => {
            const opened = row.opened;
            const target = targetFor(row);
            return (
              <div
                key={row.id}
                role={target ? 'button' : undefined}
                tabIndex={target ? 0 : undefined}
                onClick={() => target && navigate(target)}
                onKeyDown={(e) => {
                  if (target && e.key === 'Enter') navigate(target);
                }}
                className={`flex items-center justify-between p-3 border border-gray-100 rounded-xl transition-all bg-white ${
                  target ? 'hover:shadow-md hover:border-teal-200 cursor-pointer' : 'cursor-default'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      opened ? 'bg-green-50' : 'bg-gray-50'
                    }`}
                  >
                    {opened ? (
                      <MailOpen className="w-4 h-4 text-green-500" />
                    ) : (
                      <MailCheck className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-gray-900 truncate max-w-[200px] sm:max-w-[280px]">
                      {row.recipient_name || row.recipient_email}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-[200px] sm:max-w-[280px]">
                      {addressFor(row)}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                  {opened ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
                      <CheckCircle2 className="w-3 h-3" />
                      Opened
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">
                      <Clock className="w-3 h-3" />
                      Sent
                    </span>
                  )}
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                    {opened
                      ? `Opened ${moment(row.opened_at).fromNow()}`
                      : `Sent ${moment(row.sent_at).fromNow()}`}
                  </span>
                  {target && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-600 whitespace-nowrap">
                      <ExternalLink className="w-3 h-3" />
                      {row.envelope_id ? 'Open contract' : 'Open property'}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Preview toggle — kept outside the scroll window so it's always visible. */}
      {rows.length > PREVIEW_COUNT && (
        <button
          onClick={() => setShowAll((s) => !s)}
          className="mt-2 w-full text-center text-xs font-semibold text-teal-600 hover:text-teal-700 py-2 hover:underline"
        >
          {showAll ? 'Show less' : `View all ${rows.length}`}
        </button>
      )}
    </div>
  );
};

export default SentContractsPanel;
